import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Store, Link as LinkIcon, CheckCircle, AlertCircle } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { TopBar } from '../components/TopBar';
import { useAuth } from '../contexts/AuthContext';


export const ShopifyConnectionScreen = () => {
  const [shopDomain, setShopDomain] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleConnect = async () => {
    if (!shopDomain || !accessToken) {
      toast.error('Please fill in all fields');
      return;
    }

    // Validate shop domain format
    if (!shopDomain.includes('.myshopify.com') && !shopDomain.includes('.')) {
      toast.error('Please enter a valid Shopify domain (e.g., yourstore.myshopify.com)');
      return;
    }

    setConnecting(true);

    try {
      const res = await api.post(
        `/shopify/connect`,
        {
          shop_domain: shopDomain,
          access_token: accessToken
        },
        {
          
        }
      );

      if (res.data.success) {
        setIsConnected(true);
        toast.success('✓ Shopify store connected successfully!');
      }
    } catch (error) {
      console.error('Connection error:', error);
      if (error.response?.status === 400) {
        toast.error('Invalid credentials. Please check your Shopify access token.');
      } else {
        toast.error('Failed to connect. Please try again.');
      }
    } finally {
      setConnecting(false);
    }
  };

  if (isConnected) {
    return (
      <div className="min-h-screen bg-[#F0F2F5]">
        <div className="w-full max-w-md mx-auto bg-white min-h-screen shadow-xl">
          <TopBar title="Shopify Connected" showBack={true} />

          <div className="p-6 space-y-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-8"
            >
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={48} className="text-green-600" />
              </div>
              <h1 className="text-3xl font-bold text-slate-800 mb-3">
                All Set! 🎉
              </h1>
              <p className="text-base text-[#667781] leading-relaxed">
                Your Shopify store is now connected. You can publish products directly with one click!
              </p>
            </motion.div>

            <div className="bg-blue-50 rounded-xl p-5 border border-blue-100">
              <p className="text-base text-blue-900 leading-relaxed">
                <strong>Connected Store:</strong><br />
                {shopDomain}
              </p>
            </div>

            <button
              onClick={() => navigate('/my-products')}
              className="w-full h-16 bg-[#128C7E] text-white rounded-xl text-xl font-bold active:scale-95 transition-transform shadow-md"
            >
              Go to My Products
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F2F5]">
      <div className="w-full max-w-md mx-auto bg-white min-h-screen shadow-xl">
        <TopBar title="Connect Shopify" showBack={true} />

        <div className="p-6 space-y-6">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Store size={40} className="text-[#128C7E]" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800 mb-3">
              Connect Your Shopify Store
            </h1>
            <p className="text-base text-[#667781] leading-relaxed">
              Publish products directly to your Shopify store with one click
            </p>
          </motion.div>

          {/* Form */}
          <div className="space-y-5">
            <div>
              <label className="block text-base font-semibold text-slate-800 mb-3">
                Store Domain
              </label>
              <input
                type="text"
                value={shopDomain}
                onChange={(e) => setShopDomain(e.target.value)}
                placeholder="yourstore.myshopify.com"
                className="w-full h-14 px-4 bg-gray-50 border-2 border-gray-200 rounded-xl text-base focus:ring-2 focus:ring-[#128C7E] focus:border-transparent outline-none"
                data-testid="shop-domain-input"
              />
            </div>

            <div>
              <label className="block text-base font-semibold text-slate-800 mb-3">
                Access Token
              </label>
              <input
                type="password"
                value={accessToken}
                onChange={(e) => setAccessToken(e.target.value)}
                placeholder="shpat_..."
                className="w-full h-14 px-4 bg-gray-50 border-2 border-gray-200 rounded-xl text-base focus:ring-2 focus:ring-[#128C7E] focus:border-transparent outline-none"
                data-testid="access-token-input"
              />
            </div>

            <button
              onClick={handleConnect}
              disabled={connecting}
              className="w-full h-16 bg-[#128C7E] text-white rounded-xl text-xl font-bold flex items-center justify-center gap-3 transition-transform active:scale-95 shadow-lg disabled:opacity-50"
              data-testid="connect-button"
            >
              <LinkIcon size={24} />
              {connecting ? 'Connecting...' : 'Connect Store'}
            </button>
          </div>

          {/* Help Section */}
          <div className="bg-amber-50 rounded-2xl border-2 border-amber-200 p-5">
            <div className="flex items-start gap-3">
              <AlertCircle size={24} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-bold text-amber-900 mb-2">
                  How to get Access Token:
                </h3>
                <ol className="space-y-2 text-base text-amber-900">
                  <li>1. Go to Shopify Admin → Apps</li>
                  <li>2. Create a Private App</li>
                  <li>3. Enable permissions for Products</li>
                  <li>4. Copy the Access Token</li>
                  <li>5. Paste it above</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Note */}
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
            <p className="text-sm text-blue-900 leading-relaxed">
              <strong>Note:</strong> Your credentials are stored securely. We only use them to publish products on your behalf.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
